import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import { useTabNavigationStore } from '../../store/tabNavigationStore';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { coachesApi } from '../../api/coaches';
import { groupsApi } from '../../api/groups';

export function CoachDetailScreen() {
  const { getCurrentScreen, popScreen, pushScreen } = useTabNavigationStore();
  const currentScreen = getCurrentScreen();
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId);
  const queryClient = useQueryClient();

  const coachId = currentScreen.params?.coachId as string | undefined;
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', bio: '' });
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());

  const { data: coach, isLoading: isLoadingCoach } = useQuery({
    queryKey: ['coach', coachId],
    queryFn: () => coachesApi.getById(coachId!),
    enabled: !!coachId,
  });

  const { data: coachGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['coach-groups', coachId],
    queryFn: () => coachesApi.getGroupsByCoachId(coachId!),
    enabled: !!coachId,
  });

  const { data: allGroups } = useQuery({
    queryKey: ['groups', currentAssistantId],
    queryFn: () => groupsApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  });

  useEffect(() => {
    if (coach && formData.name === '') {
      setFormData({ name: coach.name, phone: coach.phone || '', email: coach.email || '', bio: coach.bio || '' });
    }
  }, [coach]);

  useEffect(() => {
    if (coachGroups) setSelectedGroupIds(new Set(coachGroups.map((g) => g.id)));
  }, [coachGroups]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      await coachesApi.update(coachId!, { name: formData.name, phone: formData.phone || undefined, email: formData.email || undefined, bio: formData.bio || undefined });
      const currentGroupIds = new Set(coachGroups?.map((g) => g.id) || []);
      for (const groupId of currentGroupIds) {
        if (!selectedGroupIds.has(groupId)) await coachesApi.removeFromGroup(coachId!, groupId);
      }
      for (const groupId of selectedGroupIds) {
        if (!currentGroupIds.has(groupId)) await coachesApi.assignToGroup(coachId!, { group_id: groupId });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coach', coachId] });
      queryClient.invalidateQueries({ queryKey: ['coach-groups', coachId] });
      queryClient.invalidateQueries({ queryKey: ['coaches', currentAssistantId] });
      setIsEditing(false);
    },
    onError: (error) => {
      console.error('Failed to update coach:', error);
      alert('Не удалось обновить тренера. Попробуйте еще раз.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => coachesApi.delete(coachId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coaches', currentAssistantId] });
      popScreen();
    },
    onError: (error) => {
      console.error('Failed to delete coach:', error);
      alert('Не удалось удалить тренера. Попробуйте еще раз.');
    },
  });

  const handleSave = () => {
    if (!formData.name.trim()) { alert('Имя тренера обязательно'); return; }
    updateMutation.mutate();
  };

  const handleDelete = () => {
    if (confirm(`Вы уверены, что хотите удалить тренера "${coach?.name}"?`)) deleteMutation.mutate();
  };

  const toggleGroup = (groupId: string) => {
    const newSet = new Set(selectedGroupIds);
    if (newSet.has(groupId)) newSet.delete(groupId);
    else newSet.add(groupId);
    setSelectedGroupIds(newSet);
  };

  const handleBack = () => {
    if (isEditing) setIsEditing(false);
    else popScreen();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: '10px',
    border: '1px solid var(--separator)', backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)', fontSize: '17px', outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '13px', fontWeight: 500, color: 'var(--text-hint)', marginBottom: '4px',
  };

  if (isLoadingCoach || isLoadingGroups) {
    return <div className="flex items-center justify-center h-full"><p style={{ color: 'var(--text-hint)' }}>Загрузка...</p></div>;
  }

  if (!coach) {
    return <div className="p-4"><Card><p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>Тренер не найден</p></Card></div>;
  }

  if (!isEditing) {
    return (
      <div className="p-4 space-y-4 pb-24">
        <PageHeader title={coach.name} onBackClick={handleBack} />
        <Card>
          <div className="space-y-3">
            {coach.phone && (
              <div>
                <p className="text-sm" style={{ color: 'var(--text-hint)' }}>Телефон</p>
                <p style={{ color: 'var(--text-primary)' }}>{coach.phone}</p>
              </div>
            )}
            {coach.email && (
              <div>
                <p className="text-sm" style={{ color: 'var(--text-hint)' }}>Email</p>
                <p style={{ color: 'var(--text-primary)' }}>{coach.email}</p>
              </div>
            )}
            {coach.bio && (
              <div>
                <p className="text-sm" style={{ color: 'var(--text-hint)' }}>О тренере</p>
                <p className="whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{coach.bio}</p>
              </div>
            )}
            <div>
              <p className="text-sm mb-2" style={{ color: 'var(--text-hint)' }}>Группы</p>
              {coachGroups && coachGroups.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {coachGroups.map((group) => (
                    <span key={group.id} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--accent)' }}>
                      {group.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic" style={{ color: 'var(--text-hint)' }}>Не привязан к группам</p>
              )}
            </div>
            {/* Calendar button */}
            <button
              onClick={() => pushScreen('calendar', { coachId })}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-[15px] font-medium transition-colors"
              style={{ backgroundColor: 'var(--accent)', color: '#FFFFFF' }}
            >
              <Calendar size={18} />
              Расписание
            </button>
            <div className="flex gap-2 pt-4" style={{ borderTop: '1px solid var(--separator)' }}>
              <Button onClick={() => setIsEditing(true)} fullWidth>Редактировать</Button>
              <Button onClick={handleDelete} variant="danger" disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? '...' : 'Удалить'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Edit mode
  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader title="Редактировать тренера" onBackClick={handleBack} />
      <Card>
        <div className="space-y-4">
          <div><label style={labelStyle}>ФИО *</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} disabled={updateMutation.isPending} /></div>
          <div><label style={labelStyle}>Телефон</label><input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} style={inputStyle} disabled={updateMutation.isPending} /></div>
          <div><label style={labelStyle}>Email</label><input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={inputStyle} disabled={updateMutation.isPending} /></div>
          <div><label style={labelStyle}>О тренере</label><textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} rows={3} style={{ ...inputStyle, resize: 'none' }} disabled={updateMutation.isPending} /></div>
          <div>
            <label style={labelStyle}>Группы</label>
            {allGroups && allGroups.length > 0 ? (
              <div className="space-y-1 max-h-60 overflow-y-auto rounded-lg p-2" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--separator)' }}>
                {allGroups.map((group) => (
                  <label key={group.id} className="flex items-center gap-2 p-2 rounded cursor-pointer">
                    <input type="checkbox" checked={selectedGroupIds.has(group.id)} onChange={() => toggleGroup(group.id)} disabled={updateMutation.isPending} />
                    <span style={{ color: 'var(--text-primary)' }}>{group.name}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm italic" style={{ color: 'var(--text-hint)' }}>Нет доступных групп</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={updateMutation.isPending || !formData.name.trim()} fullWidth>
              {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button onClick={() => { setIsEditing(false); setFormData({ name: coach.name, phone: coach.phone || '', email: coach.email || '', bio: coach.bio || '' }); setSelectedGroupIds(new Set(coachGroups?.map((g) => g.id) || [])); }} disabled={updateMutation.isPending} variant="secondary">
              Отмена
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
